/**
 * TabletTireWorkOrder — Checklist operativa per il tecnico.
 * Touch-friendly, progress bar, chiusura ordine di lavoro.
 */
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Card, Checkbox, Button, Progress, InputNumber, Typography, Space,
  Alert, message, theme,
} from 'antd'
import { CheckCircleOutlined } from '@ant-design/icons'
import { useChiudiOrdineLavoro } from '@/hooks/useTireService'

const { Title, Text } = Typography

interface ChecklistItem {
  id: string
  label: string
  sublabel?: string
  extraInput?: boolean
}

const CHECKLIST: ChecklistItem[] = [
  { id: 'recupero', label: 'Pneumatici recuperati da scaffale', sublabel: 'Verificare posizione indicata nel sistema' },
  { id: 'smontaggio', label: 'Smontaggio set corrente', sublabel: 'Rimozione completa delle 4 ruote' },
  { id: 'montaggio', label: 'Montaggio e bilanciatura', sublabel: 'Serrare a coppia indicata + bilanciatura con macchina' },
  { id: 'profondita', label: 'Misurazione profondità battistrada', sublabel: 'Misurare e registrare il valore', extraInput: true },
  { id: 'stoccaggio', label: 'Stoccaggio set smontato + stampa QR', sublabel: 'Riporre nella posizione assegnata e stampare etichetta' },
]

export default function TabletTireWorkOrder() {
  const { token } = theme.useToken()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const chiudi = useChiudiOrdineLavoro()

  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const [profondita, setProfondita] = useState<number | null>(null)

  const completedCount = CHECKLIST.filter(item => checked[item.id]).length
  const progress = Math.round((completedCount / CHECKLIST.length) * 100)
  const allDone = completedCount === CHECKLIST.length

  const handleChiudi = async () => {
    if (!id) return
    if (!allDone) {
      message.warning('Completare tutti i punti della checklist prima di chiudere')
      return
    }
    await chiudi.mutateAsync(Number(id))
    navigate('/tablet/tire-checkin')
  }

  const toggleItem = (itemId: string) => {
    setChecked(prev => ({ ...prev, [itemId]: !prev[itemId] }))
  }

  return (
    <div style={{ minHeight: '100vh', background: token.colorBgLayout, paddingBottom: 100 }}>
      {/* Header con progress */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: token.colorBgContainer,
          padding: '16px 20px 12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          zIndex: 100,
        }}
      >
        <Title level={4} style={{ margin: 0, marginBottom: 8 }}>
          Ordine #{id} — Checklist
        </Title>
        <Progress
          percent={progress}
          status={allDone ? 'success' : 'active'}
          strokeColor={allDone ? '#52c41a' : '#1677ff'}
          format={() => `${completedCount}/${CHECKLIST.length}`}
        />
      </div>

      {/* Checklist */}
      <div style={{ padding: '20px 16px' }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {CHECKLIST.map((item, idx) => (
            <Card
              key={item.id}
              size="small"
              style={{
                borderLeft: `4px solid ${checked[item.id] ? '#52c41a' : '#d9d9d9'}`,
                opacity: checked[item.id] ? 0.75 : 1,
                transition: 'all 0.2s',
              }}
              onClick={() => !item.extraInput && toggleItem(item.id)}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <Checkbox
                  checked={!!checked[item.id]}
                  onChange={() => toggleItem(item.id)}
                  style={{ marginTop: 3 }}
                  // Checkbox più grande su touch
                />
                <div style={{ flex: 1 }}>
                  <Text strong style={{ fontSize: 17, display: 'block' }}>
                    {idx + 1}. {item.label}
                  </Text>
                  {item.sublabel && (
                    <Text type="secondary" style={{ fontSize: 14 }}>{item.sublabel}</Text>
                  )}
                  {item.extraInput && (
                    <div style={{ marginTop: 12 }} onClick={e => e.stopPropagation()}>
                      <InputNumber
                        size="large"
                        placeholder="mm"
                        value={profondita ?? undefined}
                        onChange={v => {
                          setProfondita(v)
                          if (v && v > 0) setChecked(prev => ({ ...prev, profondita: true }))
                          else setChecked(prev => ({ ...prev, profondita: false }))
                        }}
                        min={0}
                        max={20}
                        step={0.5}
                        addonAfter="mm"
                        style={{ width: 160 }}
                      />
                    </div>
                  )}
                </div>
                {checked[item.id] && (
                  <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a', flexShrink: 0 }} />
                )}
              </div>
            </Card>
          ))}
        </Space>

        {allDone && (
          <Alert
            type="success"
            showIcon
            message="Tutti i punti completati. Puoi chiudere l'ordine di lavoro."
            style={{ marginTop: 20 }}
          />
        )}
      </div>

      {/* Bottone fisso in fondo */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '12px 16px',
          background: token.colorBgContainer,
          boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <Button
          type="primary"
          block
          size="large"
          disabled={!allDone}
          loading={chiudi.isPending}
          onClick={handleChiudi}
          style={{
            height: 56,
            fontSize: 18,
            background: allDone ? '#52c41a' : undefined,
            borderColor: allDone ? '#52c41a' : undefined,
          }}
          icon={<CheckCircleOutlined />}
        >
          Chiudi Ordine di Lavoro
        </Button>
      </div>
    </div>
  )
}
